import React from "react";
import { useTinyHost } from "@tiny/host-contract";
import { useForm } from "react-hook-form";
import { useApi } from "@tiny/api";
import { useMessages } from "@tiny/components";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { AddSegmentDialogProps } from "./AddSegmentDialog";
import {
  usePostExportToStaticSegment,
  usePostSegmentCustomMutation,
} from "@tiny/api";
import { emptyFilter } from "./AddSegmentDialog.helpers";
import { closeSnackbar } from "notistack";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";

interface FormValues {
  name: string;
}

export function useAddSegmentDialog(props: AddSegmentDialogProps) {
  const api = useApi();
  const { onClose, mode = "create", segmentIds } = props;

  const { navigate } = useTinyHost();

  const form = useForm<FormValues>();

  const { showErrorMessage } = useMessages();

  const [executing, setExecuting] = React.useState(false);

  const { mutateAsync: postSegmentCustomMutateAsync } =
    usePostSegmentCustomMutation();

  const { mutateAsync: postExportToStaticSegment } =
    usePostExportToStaticSegment();

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      setExecuting(true);

      if (mode === "create") {
        try {
          const { data: exist } = await api.putSegmentCheckExists({
            name: values.name,
            type: "CUSTOM",
          });
          if (exist) {
            form.setError("name", {
              type: "validate",
              message: "Segment with this name already exists!",
            });
          } else {
            const { id } = await postSegmentCustomMutateAsync({
              data: {
                name: values.name,
                filter: emptyFilter,
              },
            });
            onClose();
            navigate.to(`/main/audience/edit-segments/${id}`, {
              state: {
                segmentName: values.name,
              },
            });
          }
        } catch (e: any) {
          extractBackendError(e.response).then((be) =>
            showErrorMessage(getErrorMessage(be))
          );
        } finally {
          setExecuting(false);
        }
      } else {
        try {
          const { data: exist } = await api.putSegmentCheckExists({
            name: values.name,
            type: "UPLOAD",
          });
          if (exist) {
            form.setError("name", {
              type: "validate",
              message: "Segment with this name already exists!",
            });
          } else {
            await postExportToStaticSegment({
              data: {
                segmentIds: segmentIds,
                staticSegmentName: values.name,
              },
            });
            showUploadMessage({
              variant: "success",
              message: "Export Started",
              description: `The custom segment [ ${values.name} ] is being exported as source. You can refresh the page to check the status once the process is complete.`,
              primaryCtaActionText: "Got it",
              primaryCtaActionFunction: () => {
                closeSnackbar();
              },
              persist: false,
            });
            onClose();
            navigate.to("/main/audience?tab=sources");
          }
        } catch (e: any) {
          extractBackendError(e.response).then((be) =>
            showErrorMessage(getErrorMessage(be))
          );
        } finally {
          setExecuting(false);
        }
      }
    },
    [
      form,
      history,
      mode,
      onClose,
      postExportToStaticSegment,
      postSegmentCustomMutateAsync,
      segmentIds,
      showErrorMessage,
    ]
  );

  const continueAdding = React.useCallback(() => {
    form.handleSubmit(handleSubmit)();
  }, [form, handleSubmit]);

  return {
    form,
    handleSubmit,
    continueAdding,
    executing,
  };
}
